/**
 * Protected route component for authenticated pages.
 * 
 * @module components/ProtectedRoute
 */

import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { LoadingPage } from './ui/LoadingSpinner';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

/**
 * Protects routes from unauthorized access.
 * Redirects to login if user is not authenticated or not an admin.
 * 
 * @param props - Route props with children
 * @returns Protected content or redirect
 */
export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isLoading, isAdmin, user } = useAuth();
  const location = useLocation();

  // Show loading while checking auth state
  if (isLoading) {
    return <LoadingPage />;
  }

  // Redirect to login if not authenticated
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Redirect to login if authenticated but not an admin
  if (!isAdmin) {
    return <Navigate to="/login" state={{ from: location, unauthorized: true }} replace />;
  }

  return <>{children}</>;
}
